import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Card,
  CardContent,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Modal,
  FormHelperText,
  Checkbox,
  ListItemText,
  useMediaQuery,
  useTheme as useMuiTheme,
  AlertTitle
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import FunctionSelector from './FunctionSelector';
import WorkflowBuilder from './WorkflowBuilder';
import IntegratedAgentPlayground from './IntegratedAgentPlayground';
import { useAuth } from '../context/AuthContext';


const AgentConfig = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNewAgent = !id;
  const { user } = useAuth();
  
  // Check if we should auto-start the test
  const autoStartTest = location.state?.autoStartTest || false;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [configType, setConfigType] = useState('manual'); // 'manual' or 'workflow'
  // Add state for JSON editor
  const [workflowJsonEditor, setWorkflowJsonEditor] = useState('{}');
  const [jsonError, setJsonError] = useState(null);
 
  // Add state for JSON editor modal
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
 
  // Agent configuration state
  const [agentConfig, setAgentConfig] = useState({
    name: '',
    agent_type: 'inbound', // Default to inbound agent
    prompt_text: 'You are a helpful assistant.',
    initial_message: 'Hello, how can I help you today?',
    language: 'en-US',
    voice: 'en-US-JennyNeural',
    llm_model: 'gpt-4o',
    sip_uri: '',
    knowledge_base: '',
    kb_description: '',
    configuration_type: 'manual', // Default to manual configuration
    workflow_config: null,
    has_calendar: false, // New field for calendar functionality
    calendar_ids: [], // Changed from calendar_id to calendar_ids array
    client_configurable: false, // Whether clients can configure this agent
    owner_id: null,
    is_external: false,
  });
  
  // Calendar state
  const [enableCalendar, setEnableCalendar] = useState(false);
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [calendarError, setCalendarError] = useState('');
 
  // Add state to track if user is the agent owner
  const [isAgentOwner, setIsAgentOwner] = useState(false);
 
  // Available options
  const languageOptions = [
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'en-AU', label: 'English (Australia)' },
    { value: 'es-ES', label: 'Spanish' },
    { value: 'fr-FR', label: 'French' },
    { value: 'de-DE', label: 'German' },
    { value: 'it-IT', label: 'Italian' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)' },
    { value: 'nl-NL', label: 'Dutch' },
    { value: 'ja-JP', label: 'Japanese' },
    { value: 'ko-KR', label: 'Korean' },
    { value: 'zh-CN', label: 'Chinese (Simplified)' },
    { value: 'ru-RU', label: 'Russian' },
    { value: 'ar-SA', label: 'Arabic' },
  ];
 
  const agentTypeOptions = [
    { value: 'inbound', label: 'Inbound Agent (receives calls)' },
    { value: 'outbound', label: 'Outbound Agent (makes calls)' },
  ];
 
  const getVoiceOptions = (language) => {
    const voiceMap = {
      'en-US': [
        { value: 'en-US-JennyNeural', label: 'Jenny (Female)' },
        { value: 'en-US-GuyNeural', label: 'Guy (Male)' },
        { value: 'en-US-AriaNeural', label: 'Aria (Female)' },
        { value: 'en-US-AmberNeural', label: 'Amber (Female)' },
      ],
      'en-GB': [
        { value: 'en-GB-LibbyNeural', label: 'Libby (Female)' },
        { value: 'en-GB-RyanNeural', label: 'Ryan (Male)' },
        { value: 'en-GB-MiaNeural', label: 'Mia (Female)' },
      ],
      'en-AU': [
        { value: 'en-AU-NatashaNeural', label: 'Natasha (Female)' },
        { value: 'en-AU-WilliamNeural', label: 'William (Male)' },
      ],
      'fr-FR': [
        { value: 'fr-FR-DeniseNeural', label: 'Denise (Female)' },
        { value: 'fr-FR-HenriNeural', label: 'Henri (Male)' },
      ],
      'de-DE': [
        { value: 'de-DE-KatjaNeural', label: 'Katja (Female)' },
        { value: 'de-DE-ConradNeural', label: 'Conrad (Male)' },
      ],
      'es-ES': [
        { value: 'es-ES-ElviraNeural', label: 'Elvira (Female)' },
        { value: 'es-ES-AlvaroNeural', label: 'Alvaro (Male)' },
      ],
      'it-IT': [
        { value: 'it-IT-IsabellaNeural', label: 'Isabella (Female)' },
        { value: 'it-IT-DiegoNeural', label: 'Diego (Male)' },
      ],
      'ja-JP': [
        { value: 'ja-JP-NanamiNeural', label: 'Nanami (Female)' },
        { value: 'ja-JP-KeitaNeural', label: 'Keita (Male)' },
      ],
      'zh-CN': [
        { value: 'zh-CN-XiaoxiaoNeural', label: 'Xiaoxiao (Female)' },
        { value: 'zh-CN-YunjianNeural', label: 'Yunjian (Male)' },
      ],
      'pt-BR': [
        { value: 'pt-BR-FranciscaNeural', label: 'Francisca (Female)' },
        { value: 'pt-BR-AntonioNeural', label: 'Antonio (Male)' },
      ],
      'nl-NL': [
        { value: 'nl-NL-FennaNeural', label: 'Fenna (Female)' },
        { value: 'nl-NL-MaartenNeural', label: 'Maarten (Male)' },
      ],
      'ru-RU': [
        { value: 'ru-RU-SvetlanaNeural', label: 'Svetlana (Female)' },
        { value: 'ru-RU-DmitryNeural', label: 'Dmitry (Male)' },
      ],
      'ko-KR': [
        { value: 'ko-KR-SunHiNeural', label: 'SunHi (Female)' },
        { value: 'ko-KR-InJoonNeural', label: 'InJoon (Male)' },
      ],
      'ar-SA': [
        { value: 'ar-SA-ZariyahNeural', label: 'Zariyah (Female)' },
        { value: 'ar-SA-HamedNeural', label: 'Hamed (Male)' },
      ],
    };
   
    return voiceMap[language] || voiceMap['en-US'];
  };
 
  const modelOptions = [
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    { value: 'gpt-3.5-turbo-16k', label: 'GPT-3.5 Turbo (16k)' },
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4.5-preview', label: 'GPT-4.5 Preview' },
    { value: 'o1-preview', label: 'o1 Preview' },
    { value: 'o1-mini', label: 'o1 Mini' },
  ];
 
  // Define showSnackbar function before using it in useEffect
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleBack = () => {
    navigate('/agents');
  };
 
  // Redirect if not authorized to manage agents
  useEffect(() => {
    // We won't do an immediate redirect anymore
    // Instead, we'll let the backend check permissions
    // and will only redirect if the API call fails
    
    // We'll check permission in the fetchAgentData function below
  }, [user, navigate]);
  
  // Function to fetch agent data
  const fetchAgentData = async () => {
    if (!id) return; // Skip for new agents
    
    setLoading(true);
    try {
      console.log(`Fetching agent configuration for ID: ${id}`);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agent-creation/agent/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      console.log(`Fetch response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: ${errorText}`);
        
        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        
        // If permission error, redirect and show message
        if (response.status === 403) {
          navigate('/agents');
          showSnackbar(errorData.error || 'You do not have permission to configure this agent', 'error');
          return;
        }
        
        throw new Error(errorData.error || `Failed to fetch agent data: ${response.status}`);
      }
      
      const data = await response.json();
        console.log("Loaded agent data:", data);
        setAgentConfig({
          name: data.name || '',
          agent_type: data.agent_type || 'inbound',
          prompt_text: data.prompt_text || 'You are a helpful assistant.',
          initial_message: data.initial_message || 'Hello, how can I help you today?',
          language: data.language || 'en-US',
          voice: data.voice || 'en-US-JennyNeural',
          llm_model: data.llm_model || 'gpt-4o',
          sip_uri: data.sip_uri || '',
          knowledge_base: data.knowledge_base || '',
          kb_description: data.kb_description || '',
          configuration_type: data.configuration_type || 'manual',
          workflow_config: data.workflow_config || null,
          has_calendar: data.has_calendar || false,
        calendar_ids: data.calendar_ids || [],
        client_configurable: data.client_configurable || false,
        owner_id: data.owner_id || null,
        is_external: data.is_external || false,
      });
      
      // Check if current user is the agent owner
      if (user) {
        // Admin is always considered an owner
        if (user.role === 'admin') {
          setIsAgentOwner(true);
        } else if (data.owner_id) {
          // Non-admin: check if user is the actual owner
          setIsAgentOwner(user.id === data.owner_id);
        } else {
          // No owner_id specified, only admin can modify client_configurable
          setIsAgentOwner(false);
        }
      } else {
        setIsAgentOwner(false);
      }
        
        // Update calendar state
        setEnableCalendar(data.has_calendar || false);
        
        // If calendar is enabled, fetch available calendars
        if (data.has_calendar) {
          fetchAvailableCalendars();
        }
        
        // Set the config type based on loaded data
        setConfigType(data.configuration_type || 'manual');
        
        // Initialize JSON editor if workflow config exists
        if (data.workflow_config) {
          setWorkflowJsonEditor(JSON.stringify(data.workflow_config, null, 2));
        }
    } catch (error) {
        console.error('Error fetching agent:', error);
      showSnackbar(`Error loading agent configuration: ${error.message}`, 'error');
    } finally {
        setLoading(false);
    }
  };

  // Load agent data if editing existing agent
  useEffect(() => {
    if (!isNewAgent) {
      fetchAgentData();
    }
  }, [id, isNewAgent]);
 
  // Update voice when language changes
  useEffect(() => {
    const voices = getVoiceOptions(agentConfig.language);
    if (voices.length > 0 && !voices.some(v => v.value === agentConfig.voice)) {
      setAgentConfig(prev => ({
        ...prev,
        voice: voices[0].value
      }));
    }
  }, [agentConfig.language, agentConfig.voice]);
 
  const handleChange = (field) => (event) => {
    setAgentConfig({
      ...agentConfig,
      [field]: event.target.value,
    });
  };
  
  const handleConfigTypeChange = (event, newValue) => {
    setConfigType(newValue);
    setAgentConfig(prev => ({
      ...prev,
      configuration_type: newValue
    }));
  };

  // Add function to open JSON editor with latest workflow config
  const openJsonEditor = () => {
    console.log("Opening JSON editor with current workflow config:", agentConfig.workflow_config);
    
    // Check if we have a workflow configuration
    if (agentConfig.workflow_config && 
        (agentConfig.workflow_config.nodes?.length > 0 || 
         agentConfig.workflow_config.edges?.length > 0)) {
      // Use the existing configuration but remove position data for simplicity
      const editorVersion = JSON.parse(JSON.stringify(agentConfig.workflow_config));
      
      // Remove position data from nodes
      if (editorVersion.nodes) {
        editorVersion.nodes = editorVersion.nodes.map(node => {
          const { position, ...nodeWithoutPosition } = node;
          return nodeWithoutPosition;
        });
      }
      
      setWorkflowJsonEditor(JSON.stringify(editorVersion, null, 2));
    } else {
      // If no workflow config exists in agentConfig, initialize with a default structure
      const defaultWorkflow = {
        nodes: [
          {
            id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
            name: "Initial Agent",
            instructions: "You are a helpful voice assistant.",
            language: "en-US",
            voice: "en-US-JennyNeural",
            is_initial: true,
            initial_message: "Hello, how can I help you today?",
            tools: []
            // No position data here - the builder will handle that
          }
        ],
        edges: []
      };
      
      // Add position for the actual workflow but not in the editor
      const workflowWithPositions = {
        ...defaultWorkflow,
        nodes: defaultWorkflow.nodes.map(node => ({
          ...node,
          position: { x: 250, y: 150 }
        }))
      };
      
      // Update the editor with simplified version (no positions)
      setWorkflowJsonEditor(JSON.stringify(defaultWorkflow, null, 2));
      
      // Update the main workflow config with positions
      setAgentConfig(prev => ({
        ...prev,
        workflow_config: workflowWithPositions
      }));
    }
    
    // Open the editor modal
    setJsonEditorOpen(true);
  };

  const handleWorkflowUpdate = (workflowConfig) => {
    console.log("Workflow updated in builder:", workflowConfig);
    
    // Ensure workflowConfig is not null or undefined
    if (!workflowConfig) {
      workflowConfig = { nodes: [], edges: [] };
    }
    
    // Update the main agent config with the new workflow config
    setAgentConfig(prev => ({
      ...prev,
      workflow_config: workflowConfig
    }));
    
    // Update JSON editor when workflow is updated via builder
    setWorkflowJsonEditor(JSON.stringify(workflowConfig, null, 2));
  };
  
  // Update workflow from JSON editor
  const updateWorkflowFromJson = () => {
    try {
      // Parse the edited JSON
      const updatedConfig = JSON.parse(workflowJsonEditor);
      console.log("Parsed JSON from editor:", updatedConfig);
      
      // We need to preserve position data for existing nodes
      const currentWorkflow = agentConfig.workflow_config || { nodes: [], edges: [] };
      
      // Create a map of current node positions by ID
      const positionMap = {};
      if (currentWorkflow.nodes) {
        currentWorkflow.nodes.forEach(node => {
          if (node.id && node.position) {
            positionMap[node.id] = node.position;
          }
        });
      }
      
      // Apply the positions to the updated config
      const mergedConfig = {
        ...updatedConfig,
        nodes: (updatedConfig.nodes || []).map(node => {
          // If node exists in current config, use its position
          if (node.id && positionMap[node.id]) {
            return { ...node, position: positionMap[node.id] };
          }
          // For new nodes, assign a default position
          return { 
            ...node, 
            position: { x: 250 + Math.random() * 100, y: 150 + Math.random() * 100 } 
          };
        })
      };
      
      console.log("Merged workflow with positions:", mergedConfig);
      
      // Update the main agent config
      setAgentConfig(prev => ({
        ...prev,
        workflow_config: mergedConfig
      }));
      
      setJsonError(null);
      showSnackbar('Workflow configuration updated', 'success');
      setJsonEditorOpen(false);
    } catch (error) {
      setJsonError(`Invalid JSON: ${error.message}`);
    }
  };
  
  // Add function to handle JSON editor changes
  const handleJsonChange = (event) => {
    const newValue = event.target.value;
    setWorkflowJsonEditor(newValue);
    
    // Validate JSON
    try {
      JSON.parse(newValue);
      setJsonError(null);
    } catch (error) {
      setJsonError(`Invalid JSON: ${error.message}`);
    }
  };

  const handleSave = () => {
    // Validate required fields
    if (!agentConfig.name) {
      showSnackbar('Agent name is required', 'error');
      return;
    }
    
    // Validate configuration based on type
    if (configType === 'manual' && !agentConfig.prompt_text) {
      showSnackbar('Prompt text is required for manual configuration', 'error');
      return;
    }
    
    if (configType === 'workflow' && !agentConfig.workflow_config) {
      showSnackbar('Workflow configuration is required', 'error');
      return;
    }

    // Check permissions based on user role
    if (!user || (user.role !== 'admin' && user.role !== 'agency')) {
      showSnackbar('You do not have permission to save agents', 'error');
      return;
    }
   
    setSaving(true);
   
    // Log the data being sent
    console.log("Sending agent config:", agentConfig);
   
    const url = isNewAgent
      ? `${process.env.REACT_APP_API_URL}/api/agent-creation/create`
      : `${process.env.REACT_APP_API_URL}/api/agent-creation/update/${id}`;
     
    console.log("Request URL:", url);
   
    // Store the current configuration that we're trying to save
    const configToSave = { ...agentConfig };
   
    fetch(url, {
      method: isNewAgent ? 'POST' : 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(configToSave),
    })
    .then(async response => {
      console.log("Response status:", response.status);
      console.log("Response headers:", [...response.headers.entries()]);
     
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save agent');
      }
      
      return response.json();
    })
    .then(data => {
      console.log("Save successful:", data);
      showSnackbar(isNewAgent ? 'Agent created successfully' : 'Agent updated successfully');
      
      // Wait a moment for the snackbar to be visible before navigating
      setTimeout(() => {
        navigate('/agents');
      }, 1500);
    })
    .catch(error => {
      console.error('Error saving agent:', error);
      showSnackbar(error.message || 'Failed to save agent', 'error');
    })
    .finally(() => {
      setSaving(false);
    });
  };
 
  // Function to fetch available calendars
  const fetchAvailableCalendars = async () => {
    setLoadingCalendars(true);
    setCalendarError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/calendar/calendars`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch calendars');
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      if (data.calendars && Array.isArray(data.calendars)) {
        setAvailableCalendars(data.calendars);
        
        // If we have calendars and none is selected, select the primary one by default
        if (data.calendars.length > 0 && agentConfig.calendar_ids.length === 0) {
          const primaryCalendar = data.calendars.find(cal => cal.primary);
          if (primaryCalendar) {
            setAgentConfig(prev => ({
              ...prev,
              calendar_ids: [primaryCalendar.id]
            }));
          }
        }
      } else {
        setAvailableCalendars([]);
      }
    } catch (error) {
      console.error('Error fetching calendars:', error);
      setCalendarError(error.message || 'Failed to load calendars');
    } finally {
      setLoadingCalendars(false);
    }
  };
 
  // Add responsive variables
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
 
  useEffect(() => {
    console.log("User is agent owner:", isAgentOwner);
  }, [isAgentOwner]);
 
  // Check if agent is external or user doesn't have permission
  useEffect(() => {
    if (!isNewAgent && agentConfig && user) {
      // Check if agent is external
      const isExternal = agentConfig.is_external === true;
      
      // Check permissions
      const isAdmin = user.role === 'admin';
      const isAgentOwner = agentConfig.owner_id === user.id;
      const isAgency = user.role === 'agency';
      const isClientWithAccess = user.role === 'client' && agentConfig.client_configurable === true;
      
      const hasPermission = isAdmin || isAgentOwner || isAgency || isClientWithAccess;
      
      // If user doesn't have permission, redirect
      if (!hasPermission) {
        navigate('/agents');
        showSnackbar('You do not have permission to configure this agent', 'error');
      }
      
      // If agent is external AND user is not an admin/agency, show warning and disable most fields
      if (isExternal && !isAdmin && !isAgency) {
        showSnackbar('This is an external agent. Only limited configuration is available.', 'info');
      }
    }
  }, [isNewAgent, agentConfig, user, navigate]);
 
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
 
  return (
    <Box sx={{ p: isMobile ? 2 : 3, maxWidth: 1200, margin: '0 auto' }}>
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        sx={{ p: isMobile ? 2 : 3 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={() => navigate('/agents')}
            sx={{ mr: 2 }}
            disabled={saving}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant={isMobile ? "h5" : "h4"}>
              {isNewAgent ? 'Create New Agent' : `Edit ${agentConfig.agent_type === 'inbound' ? 'Inbound' : 'Outbound'} Agent`}
            </Typography>
          </Box>
          
          {/* Agent Test Component - only show for existing agents */}
          {!isNewAgent && id && (
            <Box sx={{ mr: 2 }}>
              <IntegratedAgentPlayground 
                agent={{ 
                  id: id, 
                  name: agentConfig.name || `Agent ${id}`,
                  is_plugnplay: false 
                }}
                autoStart={autoStartTest}
                onUsageUpdate={(usage) => {
                  console.log('Agent usage:', usage);
                  // Could implement usage tracking here
                }}
              />
            </Box>
          )}
          
          {/* Save Button in Top Right */}
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              minWidth: 120,
              height: 36
            }}
          >
            {saving ? 'Saving...' : 'Save Agent'}
          </Button>
        </Box>

        {/* External Agent Warning Banner */}
        {!isNewAgent && agentConfig && agentConfig.is_external && (
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            action={
              <IconButton
                color="inherit"
                size="small"
                onClick={() => navigate('/agents')}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <AlertTitle>External Agent</AlertTitle>
            This is an external agent. {user?.role === 'admin' || user?.role === 'agency' 
              ? 'As an admin, you have full configuration access.' 
              : 'Only the agent name can be edited.'}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: isMobile || isTablet ? 'column' : 'row' }}>
            {/* Main content area - full width in workflow mode or on mobile */}
            <Box sx={{ 
              flexGrow: 1, 
              pr: (configType === 'workflow' || isMobile || isTablet) ? 0 : 3, 
              width: '100%' 
            }}>
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12}>
                  <TextField
                    label="Agent Name"
                    value={agentConfig.name}
                    onChange={handleChange('name')}
                    fullWidth
                    required
                    disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                  />
                </Grid>

                {isNewAgent ? (
                  <Grid item xs={12} sx={{ display: 'none' }}>
                    <FormControl fullWidth required disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}>
                      <InputLabel>Agent Type</InputLabel>
                      <Select
                        value={agentConfig.agent_type}
                        onChange={handleChange('agent_type')}
                        label="Agent Type"
                      >
                        {agentTypeOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <Tabs
                    value={configType}
                    onChange={handleConfigTypeChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant={isMobile ? "fullWidth" : "standard"}
                    sx={{ mb: 2 }}
                  >
                    <Tab value="manual" label="MANUAL PROMPT" />
                    <Tab value="workflow" label="WORKFLOW BUILDER" />
                  </Tabs>
                  
                  {configType === 'manual' ? (
                    <TextField
                      label="Prompt Text"
                      value={agentConfig.prompt_text}
                      onChange={handleChange('prompt_text')}
                      fullWidth
                      multiline
                      rows={isMobile ? 6 : 8}
                      required
                      disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                    />
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button 
                          variant="outlined" 
                          color="primary"
                          onClick={openJsonEditor}
                          disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                          size={isMobile ? "small" : "medium"}
                        >
                          Edit JSON
                        </Button>
                      </Box>
                      <WorkflowBuilder 
                        value={agentConfig.workflow_config}
                        onChange={handleWorkflowUpdate}
                        disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                        languageOptions={languageOptions}
                        getVoiceOptions={getVoiceOptions}
                      />
                    </>
                  )}
                </Grid>

                {/* Show these sections only in manual mode */}
                {configType === 'manual' && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        label="Initial Message"
                        value={agentConfig.initial_message}
                        onChange={handleChange('initial_message')}
                        fullWidth
                        multiline
                        rows={isMobile ? 2 : 3}
                        disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                      />
                    </Grid>
                  </>
                )}

                {/* Calendar integration section - show for both manual and workflow modes */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Calendar Integration
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enableCalendar}
                          onChange={(e) => {
                            setEnableCalendar(e.target.checked);
                            setAgentConfig(prev => ({
                              ...prev,
                              has_calendar: e.target.checked
                            }));
                            if (e.target.checked && availableCalendars.length === 0) {
                              fetchAvailableCalendars();
                            }
                          }}
                          disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                        />
                      }
                      label="Enable Calendar Functionality"
                    />
                    
                    {enableCalendar && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" paragraph color="text.secondary">
                          When calendar functionality is enabled, the agent will be able to:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                          <Box component="li">
                            <Typography variant="body2" color="text.secondary">
                              Check availability in your Google Calendar
                            </Typography>
                          </Box>
                          <Box component="li">
                            <Typography variant="body2" color="text.secondary">
                              Suggest available time slots to users
                            </Typography>
                          </Box>
                          <Box component="li">
                            <Typography variant="body2" color="text.secondary">
                              Handle scheduling requests during conversations
                            </Typography>
                          </Box>
                        </Box>
                        
                        <FormControl fullWidth margin="normal" disabled={loadingCalendars || saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}>
                          <InputLabel id="calendar-select-label">Select Calendars</InputLabel>
                          <Select
                            labelId="calendar-select-label"
                            multiple
                            value={agentConfig.calendar_ids}
                            onChange={(e) => setAgentConfig(prev => ({
                              ...prev,
                              calendar_ids: e.target.value
                            }))}
                            label="Select Calendars"
                            renderValue={(selected) => {
                              const selectedCalendars = availableCalendars.filter(cal => selected.includes(cal.id));
                              return selectedCalendars.map(cal => cal.summary).join(', ');
                            }}
                          >
                            {loadingCalendars ? (
                              <MenuItem value="" disabled>Loading calendars...</MenuItem>
                            ) : availableCalendars.length > 0 ? (
                              availableCalendars.map(calendar => (
                                <MenuItem key={calendar.id} value={calendar.id}>
                                  <Checkbox checked={agentConfig.calendar_ids.indexOf(calendar.id) > -1} />
                                  <ListItemText primary={calendar.summary} />
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value="" disabled>No calendars available</MenuItem>
                            )}
                          </Select>
                          <FormHelperText>
                            {calendarError ? (
                              <span style={{ color: 'red' }}>{calendarError}</span>
                            ) : (
                              "Select which calendars the agent should use for scheduling"
                            )}
                          </FormHelperText>
                        </FormControl>
                        
                        {availableCalendars.length === 0 && !loadingCalendars && (
                          <Button 
                            variant="outlined" 
                            sx={{ mt: 1 }}
                            onClick={() => {
                              navigate('/settings');
                            }}
                            disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                          >
                            Connect Google Calendar
                          </Button>
                        )}
                        
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                          <Typography variant="body2">
                            <strong>Testing Calendar Availability:</strong> To test if a time slot is available, you can use the <code>calendar_availability</code> function in your agent prompt or workflow.
                          </Typography>
                          <Box component="pre" sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto', fontSize: '0.8rem' }}>
                            {`Example usage:
const available = await calendar_availability(
  "2025-05-20T09:00:00", "2025-05-20T10:00:00"
);
if (available.is_available) {
  // Time slot is available
} else {
  // Time slot has conflicts
  console.log(available.conflicts);
}`}
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Client Access Control section */}
                {isAgentOwner && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        Client Access Control
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ mb: 3 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={agentConfig.client_configurable}
                              onChange={(e) => {
                                setAgentConfig(prev => ({
                                  ...prev,
                                  client_configurable: e.target.checked
                                }));
                              }}
                              disabled={saving || !isAgentOwner || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                            />
                          }
                          label="Allow clients to configure this agent"
                        />
                        
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            When enabled, clients and agencies this agent is assigned to can modify its settings.
                            When disabled, they can use the agent but cannot change its configuration.
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </>
                )}

                {/* Knowledge Base section - always show regardless of mode */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Knowledge Base
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Knowledge Base Content"
                    value={agentConfig.knowledge_base}
                    onChange={handleChange('knowledge_base')}
                    placeholder="Paste your knowledge base content here (e.g., documentation, product information, FAQs, etc.)"
                    fullWidth
                    multiline
                    rows={10}
                    disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                    helperText="The agent will use this knowledge base as a reference for answering questions"
                    InputProps={{
                      sx: {
                        maxHeight: '400px',
                        overflowY: 'auto'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Knowledge Base Description"
                    value={agentConfig.kb_description}
                    onChange={handleChange('kb_description')}
                    placeholder="Describe what's in this knowledge base and how the agent should use it"
                    fullWidth
                    multiline
                    rows={3}
                    disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                    helperText="This helps the AI understand how to use the knowledge base content"
                  />
                </Grid>

                {/* Custom functions section - only show in manual mode */}
                {!isNewAgent && configType !== 'workflow' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        Custom Functions
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <FunctionSelector 
                        agentId={id} 
                        disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
                      />
                    </Grid>
                  </>
                )}

              </Grid>
            </Box>

            {/* Right sidebar - only show in manual mode and on desktop */}
            {(configType === 'workflow' || isMobile || isTablet) ? null : (
              <Box sx={{ width: '25%', minWidth: '200px' }}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
                    Configuration
                  </Typography>
                  
                  {configType === 'manual' && (
                    <>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                          Language
                        </Typography>
                        <FormControl fullWidth size="small" required disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}>
                          <Select
                            value={agentConfig.language}
                            onChange={handleChange('language')}
                            displayEmpty
                          >
                            {languageOptions.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                          Voice
                        </Typography>
                        <FormControl fullWidth size="small" required disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}>
                          <Select
                            value={agentConfig.voice}
                            onChange={handleChange('voice')}
                            displayEmpty
                          >
                            {getVoiceOptions(agentConfig.language).map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </>
                  )}

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      LLM Model
                    </Typography>
                    <FormControl fullWidth size="small" required disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}>
                      <Select
                        value={agentConfig.llm_model}
                        onChange={handleChange('llm_model')}
                        displayEmpty
                      >
                        {modelOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Paper>
              </Box>
            )}
            
            {/* On tablets and mobile, show the configuration options in a horizontal box if in manual mode */}
            {(configType === 'manual' && (isMobile || isTablet)) && (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                <FormControl fullWidth size="small" required disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={agentConfig.language}
                    onChange={handleChange('language')}
                    label="Language"
                  >
                    {languageOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth size="small" required disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}>
                  <InputLabel>Voice</InputLabel>
                  <Select
                    value={agentConfig.voice}
                    onChange={handleChange('voice')}
                    label="Voice"
                  >
                    {getVoiceOptions(agentConfig.language).map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth size="small" required disabled={saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}>
                  <InputLabel>LLM Model</InputLabel>
                  <Select
                    value={agentConfig.llm_model}
                    onChange={handleChange('llm_model')}
                    label="LLM Model"
                  >
                    {modelOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* JSON Editor Modal with responsive adjustments */}
      <Modal
        open={jsonEditorOpen}
        onClose={() => setJsonEditorOpen(false)}
        aria-labelledby="json-editor-modal"
        aria-describedby="edit-workflow-json"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: isMobile ? 2 : 4,
          width: isMobile ? '95%' : '80%',
          maxWidth: '900px',
          maxHeight: '80vh',
          borderRadius: 2,
          overflow: 'auto'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} component="h2">
              Edit Workflow JSON
            </Typography>
            <IconButton onClick={() => setJsonEditorOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <TextField
            fullWidth
            multiline
            variant="outlined"
            value={workflowJsonEditor}
            onChange={handleJsonChange}
            error={!!jsonError}
            helperText={jsonError}
            sx={{ 
              mb: 2,
              fontFamily: 'monospace',
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }
            }}
            minRows={isMobile ? 10 : 15}
            maxRows={isMobile ? 15 : 25}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => setJsonEditorOpen(false)}
              size={isMobile ? "small" : "medium"}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={updateWorkflowFromJson}
              disabled={!!jsonError || saving || (agentConfig.is_external && !(user?.role === 'admin' || user?.role === 'agency'))}
              size={isMobile ? "small" : "medium"}
            >
              Update Workflow
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AgentConfig; 